import { createStore } from 'vuex';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '@/config/firebase';
import { applyTheme } from '@/utils/theme';
import { readSupportSession, saveSupportSession, clearSupportSession } from '@/utils/supportSession';

const ACTIVE_CONTEXT_STORAGE_KEY = 'meusActiveContextV1';

const loadActiveContext = () => {
  try {
    const raw = localStorage.getItem(ACTIVE_CONTEXT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || (parsed.surface !== 'me' && parsed.surface !== 'us')) return null;
    if (parsed.surface === 'us' && !parsed.accountId) return null;
    return {
      surface: parsed.surface,
      accountId: parsed.accountId || null,
      updatedAt: parsed.updatedAt || null,
      source: parsed.source || 'unknown',
    };
  } catch (_) {
    return null;
  }
};

const persistActiveContext = (context) => {
  try {
    if (!context) {
      localStorage.removeItem(ACTIVE_CONTEXT_STORAGE_KEY);
      return;
    }
    localStorage.setItem(ACTIVE_CONTEXT_STORAGE_KEY, JSON.stringify(context));
  } catch (_) {
    // ignore storage errors
  }
};

const hasActiveMembership = async ({ uid, accountId }) => {
  if (!uid || !accountId) return false;
  try {
    const membershipRef = doc(db, `accounts/${accountId}/members/${uid}`);
    const membershipSnap = await getDoc(membershipRef);
    if (!membershipSnap.exists()) return false;
    // Compatibilidade transitória: ausência de status ainda é tratada como ativo.
    return membershipSnap.data()?.status !== 'inactive';
  } catch (_) {
    return false;
  }
};

const isPermissionDeniedError = (error) => {
  if (!error) return false;
  const code = (error.code || '').toString().toLowerCase();
  if (code === 'permission-denied' || code === 'auth/insufficient-permission') return true;
  const message = (typeof error === 'string' ? error : (error.message || '')).toString().toLowerCase();
  return message.includes('missing or insufficient permissions') || message.includes('insufficient permission');
};

export default createStore({
  state: {
    user: null,
    email: localStorage.getItem('userEmail') || null,
    company: null,
    supportSession: readSupportSession(),
    activeContext: loadActiveContext(),
  },
  mutations: {
    SET_USER(state, user) {
      state.user = user;
    },
    SET_EMAIL(state, email) {
      state.email = email;
      localStorage.setItem('userEmail', email);
    },
    CLEAR_EMAIL(state) {
      state.email = null;
      localStorage.removeItem('userEmail');
    },
    SET_COMPANY(state, company) {
      state.company = company;
    },
    SET_SUPPORT_SESSION(state, session) {
      state.supportSession = session || null;
      if (session) saveSupportSession(session);
      else clearSupportSession();
    },
    CLEAR_SUPPORT_SESSION(state) {
      state.supportSession = null;
      clearSupportSession();
    },
    SET_ACTIVE_CONTEXT(state, context) {
      state.activeContext = context;
      persistActiveContext(context);
    },
    CLEAR_ACTIVE_CONTEXT(state) {
      state.activeContext = null;
      persistActiveContext(null);
    },
  },
  actions: {
    async logIn({ commit }, { email, password }) {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      commit('SET_USER', userCredential.user);
      commit('SET_EMAIL', email);
    },
    async logOut({ commit }) {
      await auth.signOut().catch(() => {});
      try {
        localStorage.removeItem('sessionExpiresAtMs');
        localStorage.removeItem('supportLoginExpiresAtMs');
      } catch (_) { /* ignore */ }
      commit('SET_USER', null);
      commit('CLEAR_EMAIL');
      commit('SET_COMPANY', null);
      commit('CLEAR_SUPPORT_SESSION');
      commit('CLEAR_ACTIVE_CONTEXT');
      applyTheme();
    },
    async setCompany({ commit }, companyName) {
      try {
        const companyRef = doc(db, `accounts/acc_${(companyName || '').toString().trim().toLowerCase()}`);
        const companyDoc = await getDoc(companyRef);

        if (companyDoc.exists()) {
          const companyData = companyDoc.data();
          commit('SET_COMPANY', companyData);
          applyTheme(companyData.theme || {});
          return true;
        }
        commit('SET_COMPANY', null);
        applyTheme();
        return false;
      } catch (error) {
        if (!isPermissionDeniedError(error)) {
          console.error('Erro ao buscar a empresa:', error);
          commit('SET_COMPANY', null);
          applyTheme();
          return false;
        }
        return true;
      }
    },
    setSupportSession({ commit }, session) {
      commit('SET_SUPPORT_SESSION', session);
    },
    clearSupportSession({ commit }) {
      commit('CLEAR_SUPPORT_SESSION');
      applyTheme();
    },
    async setActiveContext({ commit, state }, payload) {
      const surface = (payload?.surface || '').toString();
      const accountId = (payload?.accountId || '').toString().trim();
      const source = (payload?.source || 'app').toString();

      if (surface !== 'me' && surface !== 'us') return false;
      if (surface === 'us' && !accountId) return false;

      if (surface === 'us') {
        const uid = state.user && state.user.uid ? state.user.uid : '';
        if (!uid) return false;
        const allowed = await hasActiveMembership({ uid, accountId });
        if (!allowed) return false;
      }

      commit('SET_ACTIVE_CONTEXT', {
        surface,
        accountId: surface === 'us' ? accountId : null,
        updatedAt: new Date().toISOString(),
        source,
      });
      return true;
    },
    clearActiveContext({ commit }) {
      commit('CLEAR_ACTIVE_CONTEXT');
    },
  },
  getters: {
    user: state => state.user,
    email: state => state.email,
    company: state => state.company,
    supportSession: state => state.supportSession,
    supportActive: state => {
      const s = state.supportSession;
      return !!(s && s.expiresAtMs && Date.now() < s.expiresAtMs);
    },
    userEmail: state => {
      const s = state.supportSession;
      if (s && s.expiresAtMs && Date.now() < s.expiresAtMs) {
        return s.impersonatedEmail || s.supportEmail || state.email;
      }
      return state.email;
    },
    activeContext: state => state.activeContext,
    activeSurface: state => (state.activeContext ? state.activeContext.surface : null),
    activeAccountId: state => (state.activeContext ? state.activeContext.accountId : null),
  },
});
